import { useState } from "react";
import { Check, KeyRound, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ErrorBox, Field, PageHead, Spinner, StatusBadge, useFetch } from "@/components/common";
import { CredentialDialog } from "@/components/CredentialDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ROLE_NAME } from "./salesUi";

const BLANK = { username: "", full_name: "", email: "", mobile: "", role: "sales_agent", manager_id: "" };

function AccountForm({ initial, managers, isAdmin, onSaved, onClose }) {
  const editing = Boolean(initial?.id);
  const [f, setF] = useState(editing ? { ...initial, manager_id: initial.manager_id || "", role: initial.role === "cashier" ? "sales_agent" : initial.role } : BLANK);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editing) {
        const body = { full_name: f.full_name, email: f.email, mobile: f.mobile };
        if (isAdmin) Object.assign(body, { role: f.role, manager_id: f.role === "sales_agent" ? f.manager_id : null });
        await api.patch(`/sales/accounts/${initial.id}`, body);
        toast.success("Account updated"); onSaved(null);
      } else {
        const { data } = await api.post("/sales/accounts", { ...f, manager_id: f.manager_id || null });
        onSaved({ username: data.user.username, temp_password: data.temp_password, pending: data.user.approval_status === "PENDING" });
      }
    } catch (err) { setError(errMsg(err)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl" data-testid="account-form-dialog">
        <DialogHeader><DialogTitle>{editing ? (initial.role === "cashier" ? "Convert cashier account" : "Edit account") : isAdmin ? "New sales account" : "New agent (admin approval required)"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {!editing && <Field label="Username"><input className="field font-mono lowercase" value={f.username} onChange={set("username")} required data-testid="account-username-input" /></Field>}
          <Field label="Full name"><input className="field" value={f.full_name} onChange={set("full_name")} required data-testid="account-name-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email"><input type="email" className="field" value={f.email || ""} onChange={set("email")} required data-testid="account-email-input" /></Field>
            <Field label="Mobile"><input className="field" value={f.mobile || ""} onChange={set("mobile")} required data-testid="account-mobile-input" /></Field>
          </div>
          {isAdmin && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Role"><select className="field" value={f.role} onChange={set("role")} data-testid="account-role-select"><option value="sales_agent">Sales Force</option><option value="sales_manager">Sales Manager</option></select></Field>
              {f.role === "sales_agent" && <Field label="Sales Manager"><select className="field" value={f.manager_id} onChange={set("manager_id")} required data-testid="account-manager-select"><option value="">Select…</option>{managers.map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}</select></Field>}
            </div>
          )}
          <ErrorBox error={error} />
          <button className="btn-primary w-full" data-testid="account-save-button">{editing ? "Save" : "Create account"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Row({ a, managers, isAdmin, onEdit, reload, setCreds }) {
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const reset = async () => { try { const { data } = await api.post(`/sales/accounts/${a.id}/reset-password`); setCreds({ username: a.username, temp_password: data.temp_password }); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <tr data-testid={`account-row-${a.username}`}>
      <td><b>{a.full_name}</b><br /><span className="font-mono text-[10px] text-slate-400">{a.username}</span></td>
      <td className="text-xs">{ROLE_NAME[a.role]}</td>
      <td className="text-xs">{managers.find((m) => m.id === a.manager_id)?.full_name || "—"}</td>
      <td className="text-xs">{a.email}<br />{a.mobile}</td>
      <td>{a.role === "cashier" ? <StatusBadge status="LOCKED" /> : <StatusBadge status={a.approval_status === "APPROVED" ? (a.active ? "ACTIVE" : "INACTIVE") : a.approval_status} testId={`account-status-${a.username}`} />}</td>
      <td className="text-xs">{fmtDateTime(a.created_at)}</td>
      <td><div className="flex flex-wrap gap-1">
        {isAdmin && a.approval_status === "PENDING" && <>
          <button className="btn-primary btn-sm" onClick={() => act(() => api.post(`/sales/accounts/${a.id}/approval`, { decision: "APPROVED" }), "Account approved")} data-testid={`approve-${a.username}`}><Check size={12} /> Approve</button>
          <button className="btn-secondary btn-sm" onClick={() => act(() => api.post(`/sales/accounts/${a.id}/approval`, { decision: "REJECTED" }), "Account rejected")} data-testid={`reject-${a.username}`}><X size={12} /></button></>}
        <button className="btn-ghost btn-sm" onClick={() => onEdit(a)} data-testid={`edit-${a.username}`}>{a.role === "cashier" ? "Convert" : "Edit"}</button>
        {a.role !== "cashier" && <button className="btn-ghost btn-sm" onClick={() => act(() => api.patch(`/sales/accounts/${a.id}`, { active: !a.active }), a.active ? "Deactivated" : "Activated")} data-testid={`toggle-active-${a.username}`}>{a.active ? "Deactivate" : "Activate"}</button>}
        {a.role !== "cashier" && <button className="btn-ghost btn-sm" onClick={reset} data-testid={`reset-${a.username}`}><KeyRound size={12} /></button>}
      </div></td>
    </tr>
  );
}

export default function Team() {
  const { user } = useAuth();
  const isAdmin = ["admin", "super_admin"].includes(user.role);
  const { data, reload } = useFetch("/sales/accounts");
  const [form, setForm] = useState(null);
  const [creds, setCreds] = useState(null);
  if (!data) return <Spinner />;
  const managers = isAdmin ? data.filter((a) => a.role === "sales_manager" && a.active) : [user];
  const pending = data.filter((a) => a.approval_status === "PENDING").length;
  return (
    <div className="space-y-4">
      <PageHead eyebrow="Sales Force" title={isAdmin ? "Sales team" : "My team"} sub={pending ? `${pending} account(s) awaiting admin approval` : "Create accounts, assign agents to managers and manage access."}
        actions={<button className="btn-primary" onClick={() => setForm({})} data-testid="new-account-button"><Plus size={15} /> {isAdmin ? "New account" : "New agent"}</button>} />
      <div className="surface overflow-x-auto">
        <table className="data-table" data-testid="accounts-table">
          <thead><tr><th>Name</th><th>Role</th><th>Manager</th><th>Contact</th><th>Status</th><th>Created</th><th /></tr></thead>
          <tbody>{data.map((a) => <Row key={a.id} a={a} managers={isAdmin ? data.filter((m) => m.role === "sales_manager") : [user]} isAdmin={isAdmin} onEdit={setForm} reload={reload} setCreds={setCreds} />)}</tbody>
        </table>
      </div>
      {form && <AccountForm initial={form} managers={managers} isAdmin={isAdmin} onClose={() => setForm(null)}
        onSaved={(c) => { setForm(null); reload(); if (c) { setCreds(c); if (c.pending) toast.info("Agent created — an admin must approve before they can sign in"); } }} />}
      <CredentialDialog creds={creds} onClose={() => setCreds(null)} />
    </div>
  );
}
